import { FC, useEffect, useState, useEffectEvent } from 'react'
import { useLocation } from 'react-router'
import { useDispatch, useSelector } from 'react-redux'
import axios from 'axios'
import { Button } from '@mui/material'

import { useActiveVideoVisit } from '~/shared/hooks/activeVideoVisit'
import { queueNotification } from '~/shared/notifications/actions'
import { SERVICE_VERSION } from '~/shared/lib/config'
import { logger } from '~/shared/lib/logger'
import { fireAndForget } from '~/shared/lib/fireAndForget'

// Check for updates every 15 minutes
const UPDATE_CHECK_INTERVAL = 1000 * 60 * 15

export const CheckForUpdates: FC = () => {
  const [updateAvailable, setUpdateAvailable] = useState(false)
  const userId = useSelector(state => state.me.id)

  // Leverage update check to surface notification with reload button
  // so stale tabs have a refresh button to click
  const dispatch = useDispatch()
  useEffect(() => {
    if (updateAvailable) {
      dispatch(
        queueNotification({
          persist: true,
          variant: 'info',
          message: 'An update is available',
          action: (
            <Button color="inherit" size="small" onClick={() => window.location.reload()}>
              Refresh
            </Button>
          ),
        }),
      )
    }
  }, [updateAvailable, dispatch])

  useEffect(() => {
    const check = async () => {
      if (await checkForUpdate()) {
        setUpdateAvailable(true)
        clearInterval(interval)
      }
    }
    const interval = setInterval(
      () => fireAndForget('[Update] checkForUpdate', check),
      UPDATE_CHECK_INTERVAL,
    )

    return function cleanup() {
      if (interval) {
        clearInterval(interval)
      }
    }
  }, [])

  // If an update is required, load the next view using a full page refresh
  // This reloads after the navigation; `useNavigation` could reload before it, but it needs a
  // data router and the app mounts `BrowserRouter`.
  const location = useLocation()
  const { activeVideoVisitId } = useActiveVideoVisit()

  // Only force a refresh if navigating to a different view
  // This is to prevent reloads when a clinician is doing
  // something like changing a worklist or task filter
  const currentRoute = getRootRoute(location.pathname)
  // Runs only when the root route changes: reloading is a navigation-time decision, so
  // `updateAvailable` and the active-visit id are read through the event rather than depended on,
  // which would reload mid-view.
  const reloadIfUpdateAvailable = useEffectEvent(() => {
    try {
      // Prevent a reload when a clinician is in an active video visit
      const activeAppointment = activeVideoVisitId !== null

      if (updateAvailable && !activeAppointment) {
        logger.debug('[Session] Forced reload for User', userId, 'from CheckForUpdates')
        window.location.reload()
      }
    } catch (e) {
      // Bail out in case of any errors
      if (e instanceof Error) {
        logger.error('[Update] check for updates', e)
      }
    }
  })
  useEffect(() => {
    reloadIfUpdateAvailable()
  }, [currentRoute])

  return null
}

// Fetch fresh copy of index.html and check its git hash against the current one
const checkForUpdate = async () => {
  // Add timestamp to prevent any response cache
  const { data: html } = await axios.get<string>(`/?timestamp=${new Date().getTime()}`)

  const newServiceVersion = html.match(/data-service-version\s*=\s*['"](\w*)['"]/)?.[1]

  return newServiceVersion && newServiceVersion !== SERVICE_VERSION
}

// Gets the top level route, i.e. '/patients/1754/careplans' => 'patients'
const getRootRoute = (route: string) => route.split('/').filter(Boolean)[0]
